// Anwesenheits-Reklamationen (Eltern/Spieler ↔ Trainer)

import { getSupabaseClient } from "../supabase/index.js";
import { normalizeSessionPersonIdList } from "./session-rsvp.js";

function __asStr(x) {
  return String(x ?? "").trim();
}

function __rpcJsonbArray(data) {
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  return [];
}

/** @readonly */
export const ATTENDANCE_DISPUTE_CLAIM_REASON_CODES = Object.freeze([
  "illness",
  "prior_commitment",
  "rsvp_declined",
  "not_at_location",
  "other",
]);

/** @readonly */
export const ATTENDANCE_DISPUTE_ACCEPT_REASON_CODES = Object.freeze([
  "verified_absence",
  "billing_adjusted",
  "accepted_other",
]);

/** @readonly */
export const ATTENDANCE_DISPUTE_REJECT_REASON_CODES = Object.freeze([
  "attended_confirmed",
  "insufficient_evidence",
  "billing_locked",
  "rejected_other",
]);

/**
 * @param {object|null|undefined} session
 * @param {string} personId
 */
export function isPersonMarkedPresentForSession(session, personId) {
  const pid = __asStr(personId);
  if (!pid) return false;
  return normalizeSessionPersonIdList(session?.present_ids).includes(pid);
}

/**
 * @param {object|null|undefined} dispute
 */
export function normalizeAttendanceDispute(dispute) {
  if (!dispute || typeof dispute !== "object") return null;
  const status = __asStr(dispute.status);
  if (!status) return null;
  return {
    id: __asStr(dispute.id),
    session_id: __asStr(dispute.session_id),
    person_id: __asStr(dispute.person_id),
    status,
    claim_reason_code: __asStr(dispute.claim_reason_code),
    trainer_response_reason_code: __asStr(dispute.trainer_response_reason_code),
    trainer_response_note: __asStr(dispute.trainer_response_note),
    student_acknowledged_at: dispute.student_acknowledged_at ?? null,
    submitted_at: dispute.submitted_at ?? null,
    resolved_at: dispute.resolved_at ?? null,
  };
}

/**
 * @param {object|null|undefined} session
 * @param {string} personId
 * @returns {'submit'|'pending'|'accepted'|'rejected'|'billing_locked'|'none'}
 */
export function resolveParentPortalAttendanceDisputeMode(session, personId) {
  const pid = __asStr(personId);
  if (!pid || !session?.attendance_confirmed) return "none";
  if (!isPersonMarkedPresentForSession(session, pid)) return "none";

  if (session?.billing_locked === true) {
    const dispute = normalizeAttendanceDispute(session?.attendance_dispute);
    if (dispute?.status === "pending") return "pending";
    if (dispute?.status === "accepted") return "accepted";
    if (dispute?.status === "rejected") return "rejected";
    return "billing_locked";
  }

  const dispute = normalizeAttendanceDispute(session?.attendance_dispute);
  if (!dispute) return "submit";
  if (dispute.status === "pending") return "pending";
  if (dispute.status === "accepted") return "accepted";
  if (dispute.status === "rejected") return "rejected";
  return "none";
}

/**
 * @param {unknown} reason
 * @returns {string} i18n key
 */
export function resolveAttendanceDisputeSubmitErrorKey(reason) {
  switch (__asStr(reason)) {
    case "session-not-confirmed":
      return "trainerstunden.family.dispute.errors.notConfirmed";
    case "person-not-marked-present":
      return "trainerstunden.family.dispute.errors.notMarkedPresent";
    case "dispute-already-pending":
      return "trainerstunden.family.dispute.errors.alreadyPending";
    case "dispute-already-accepted":
      return "trainerstunden.family.dispute.errors.alreadyAccepted";
    case "billing-locked":
      return "trainerstunden.family.dispute.errors.billingLocked";
    case "invalid-reason":
    case "missing-params":
      return "trainerstunden.family.dispute.errors.validation";
    case "person-not-allowed":
      return "trainerstunden.family.booking.errors.notAllowed";
    default:
      return "trainerstunden.family.dispute.errors.failed";
  }
}

/**
 * @param {unknown} reason
 * @returns {string} i18n key
 */
export function resolveAttendanceDisputeResolveErrorKey(reason) {
  switch (__asStr(reason)) {
    case "billing-locked":
      return "trainerstunden.attendanceDispute.resolve.errors.billingLocked";
    case "dispute-not-pending":
      return "trainerstunden.attendanceDispute.resolve.errors.notPending";
    case "invalid-accept-reason":
    case "invalid-reject-reason":
    case "missing-params":
      return "trainerstunden.attendanceDispute.resolve.errors.validation";
    default:
      return "trainerstunden.attendanceDispute.resolve.errors.failed";
  }
}

/**
 * @param {Array<object>|null|undefined} disputes
 */
export function listPendingAttendanceDisputes(disputes) {
  return (Array.isArray(disputes) ? disputes : [])
    .map((row) => normalizeAttendanceDispute(row))
    .filter((row) => row && row.status === "pending");
}

/**
 * Nur Reklamationen zu Sessions, die der aktuelle Nutzer (RLS) laden darf.
 * @param {Array<object>|null|undefined} disputes
 * @param {Array<object>|null|undefined} sessions
 */
export function scopeAttendanceDisputesToVisibleSessions(disputes, sessions) {
  const sessionIds = new Set(
    (Array.isArray(sessions) ? sessions : []).map((s) => __asStr(s?.id)).filter(Boolean)
  );
  if (!sessionIds.size) return [];
  return (Array.isArray(disputes) ? disputes : [])
    .map((row) => normalizeAttendanceDispute(row))
    .filter((row) => row && sessionIds.has(row.session_id));
}

/**
 * @param {string} sessionId
 * @param {Array<object>|null|undefined} disputes
 */
export function sessionHasPendingAttendanceDispute(sessionId, disputes) {
  const sid = __asStr(sessionId);
  if (!sid) return false;
  return listPendingAttendanceDisputes(disputes).some((row) => row.session_id === sid);
}

/**
 * Session darf solange nicht abgerechnet werden, wie mindestens eine Reklamation offen ist.
 * @param {object|null|undefined} session
 * @param {Array<object>|null|undefined} disputes
 */
export function isSessionBlockedForBillingByAttendanceDispute(session, disputes) {
  const sid = __asStr(session?.id);
  if (!sid) return false;
  return sessionHasPendingAttendanceDispute(sid, disputes);
}

/**
 * @param {Array<object>} sessions
 * @param {Array<object>} disputes
 */
export function indexPendingAttendanceDisputesBySessionId(sessions, disputes) {
  const sessionIds = new Set(
    (Array.isArray(sessions) ? sessions : []).map((s) => __asStr(s?.id)).filter(Boolean)
  );
  const map = new Map();
  for (const dispute of listPendingAttendanceDisputes(disputes)) {
    if (!sessionIds.has(dispute.session_id)) continue;
    const list = map.get(dispute.session_id) || [];
    list.push(dispute);
    map.set(dispute.session_id, list);
  }
  return map;
}

/**
 * @param {Array<object>|null|undefined} sessions
 * @returns {Array<{ session: object, dispute: ReturnType<typeof normalizeAttendanceDispute> }>}
 */
export function collectUnacknowledgedAttendanceDisputeNotices(sessions) {
  const out = [];
  for (const session of Array.isArray(sessions) ? sessions : []) {
    const dispute = normalizeAttendanceDispute(session?.attendance_dispute);
    if (!dispute) continue;
    if (dispute.status !== "accepted" && dispute.status !== "rejected") continue;
    if (!__asStr(dispute.trainer_response_note)) continue;
    if (dispute.student_acknowledged_at) continue;
    out.push({ session, dispute });
  }
  return out;
}

/**
 * @param {Array<object>|null|undefined} metaRows
 */
export function mergeParentPortalAttendanceMetaRows(metaRows) {
  const metaBySession = new Map();
  for (const row of Array.isArray(metaRows) ? metaRows : []) {
    const sid = __asStr(row?.session_id);
    if (!sid) continue;
    const prev = metaBySession.get(sid);
    if (!prev) {
      metaBySession.set(sid, row);
      continue;
    }
    metaBySession.set(sid, {
      ...prev,
      billing_locked: Boolean(prev.billing_locked) || Boolean(row.billing_locked),
      attendance_dispute: prev.attendance_dispute ?? row.attendance_dispute,
    });
  }
  return metaBySession;
}

/**
 * @param {Array<object>|null|undefined} metaRows
 */
export function mergeParentPortalAttendanceMetaIntoSessions(sessions, metaRows) {
  const metaBySession = mergeParentPortalAttendanceMetaRows(metaRows);

  return (Array.isArray(sessions) ? sessions : []).map((session) => {
    const sid = __asStr(session?.id);
    const meta = sid ? metaBySession.get(sid) : null;
    if (!meta) return session;
    return {
      ...session,
      present_ids: normalizeSessionPersonIdList(meta.present_ids ?? session.present_ids),
      present_despite_decline_ids: normalizeSessionPersonIdList(
        meta.present_despite_decline_ids ?? session.present_despite_decline_ids
      ),
      billing_locked: Boolean(meta.billing_locked) || Boolean(session.billing_locked),
      attendance_dispute:
        normalizeAttendanceDispute(meta.attendance_dispute) ??
        normalizeAttendanceDispute(session.attendance_dispute),
    };
  });
}

/**
 * @param {string[]} personIds
 */
export async function loadParentPortalSessionAttendanceMetaForPeopleRemote(personIds) {
  const ids = [...new Set((Array.isArray(personIds) ? personIds : []).map(__asStr).filter(Boolean))];
  if (!ids.length) return { ok: true, data: [] };

  const parts = await Promise.all(ids.map((id) => loadParentPortalSessionAttendanceMetaRemote(id)));
  const failed = parts.find((p) => !p?.ok);
  if (failed) return failed;

  const merged = [];
  for (const part of parts) {
    merged.push(...(Array.isArray(part?.data) ? part.data : []));
  }
  return { ok: true, data: merged };
}

export async function loadParentPortalSessionAttendanceMetaRemote(personId) {
  const pid = __asStr(personId);
  if (!pid) return { ok: true, data: [] };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc("trainerstunden_parent_portal_session_attendance_meta", {
      p_person_id: pid,
    });

    if (error) return { ok: false, reason: "rpc-error", error };
    const rows = __rpcJsonbArray(data);
    return { ok: true, data: rows };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

/**
 * Fakturierte Session-IDs für Kinder (Fallback, wenn Meta-RPC veraltet ist).
 * @param {string[]} personIds
 */
export async function loadParentPortalBillingLockedSessionIdsRemote(personIds) {
  const ids = [...new Set((Array.isArray(personIds) ? personIds : []).map(__asStr).filter(Boolean))];
  if (!ids.length) return { ok: true, data: [] };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc("trainerstunden_parent_portal_billing_locked_session_ids", {
      p_person_ids: ids,
    });

    if (error) {
      const code = String(error?.code || "");
      if (code === "PGRST202" || code === "42883") {
        return { ok: true, data: [] };
      }
      return { ok: false, reason: "rpc-error", error };
    }
    return { ok: true, data: __rpcJsonbArray(data).map(__asStr).filter(Boolean) };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

/**
 * @param {object[]} sessions
 * @param {string[]} lockedSessionIds
 */
export function applyParentPortalBillingLockedToSessions(sessions, lockedSessionIds) {
  const locked = new Set(
    (Array.isArray(lockedSessionIds) ? lockedSessionIds : []).map(__asStr).filter(Boolean)
  );
  if (!locked.size) return Array.isArray(sessions) ? sessions : [];
  return (Array.isArray(sessions) ? sessions : []).map((session) => {
    const sid = __asStr(session?.id);
    if (!sid || !locked.has(sid)) return session;
    return { ...session, billing_locked: true };
  });
}

export async function submitParentPortalAttendanceDisputeRemote({ sessionId, personId, claimReasonCode }) {
  const sid = __asStr(sessionId);
  const pid = __asStr(personId);
  const reason = __asStr(claimReasonCode);
  if (!sid || !pid || !reason) return { ok: false, reason: "missing-params" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc("trainerstunden_parent_portal_submit_attendance_dispute", {
      p_session_id: sid,
      p_person_id: pid,
      p_claim_reason_code: reason,
    });

    if (error) return { ok: false, reason: "rpc-error", error };
    if (!data || typeof data !== "object") return { ok: false, reason: "no-data" };
    if (data.ok === false) return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return { ok: true, dispute: normalizeAttendanceDispute(data.dispute) };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export async function resolveAttendanceDisputeRemote({ disputeId, accept, responseReasonCode, responseNote = "" }) {
  const id = __asStr(disputeId);
  const reason = __asStr(responseReasonCode);
  const note = __asStr(responseNote);
  if (!id || !reason) return { ok: false, reason: "missing-params" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc("trainerstunden_resolve_attendance_dispute", {
      p_dispute_id: id,
      p_accept: Boolean(accept),
      p_response_reason_code: reason,
      p_response_note: note || null,
    });

    if (error) return { ok: false, reason: "rpc-error", error };
    if (!data || typeof data !== "object") return { ok: false, reason: "no-data" };
    if (data.ok === false) return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return {
      ok: true,
      dispute: normalizeAttendanceDispute(data.dispute),
      session: data.session && typeof data.session === "object" ? data.session : null,
    };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export async function acknowledgeAttendanceDisputeNoteRemote({ disputeId }) {
  const id = __asStr(disputeId);
  if (!id) return { ok: false, reason: "missing-params" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc("trainerstunden_parent_portal_acknowledge_attendance_dispute_note", {
      p_dispute_id: id,
    });

    if (error) return { ok: false, reason: "rpc-error", error };
    if (!data || typeof data !== "object") return { ok: false, reason: "no-data" };
    if (data.ok === false) return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return { ok: true, dispute: normalizeAttendanceDispute(data.dispute) };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export async function loadAttendanceDisputesRemote({ status = "pending" } = {}) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc("trainerstunden_list_attendance_disputes", {
      p_status: __asStr(status) || "pending",
    });

    if (error) {
      const code = String(error?.code || "");
      if (code === "PGRST202" || code === "42883") {
        return { ok: true, data: [] };
      }
      return { ok: false, reason: "rpc-error", error };
    }
    const rows = __rpcJsonbArray(data);
    return { ok: true, data: rows.map((row) => normalizeAttendanceDispute(row)).filter(Boolean) };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}
